// Screenshot / chart analysis prompt.
// Used when Sir uploads a chart image.
// Appended to the user message before sending to Claude Vision.

export const buildAnalysisPrompt = (userContext = '') => `
${userContext ? userContext + '\n\n' : ''}Please analyze this XAU/USD chart screenshot in full detail.

Perform a complete ICT/SMC analysis:

1. CHART IDENTIFICATION:
   - What timeframe is this?
   - What is the approximate current price?
   - What is the overall market structure (Bullish / Bearish / Ranging)?

2. ICT / SMC CONCEPTS VISIBLE:
   - Are there any Order Blocks? (Last opposing candle before impulse)
   - Any Fair Value Gaps (imbalances)?
   - Any obvious Liquidity levels (equal highs/lows)?
   - Any BOS or CHoCH?
   - Which phase of AMD cycle is this? (Accumulation / Manipulation / Distribution)

3. FIBONACCI ANALYSIS:
   - If swing points are visible, what are the key Fibonacci levels?
   - Is price currently in the OTE zone (0.618–0.786)?
   - Is price in Premium or Discount zone?

4. CONFLUENCE SCORING:
   Score the current setup out of 10 using:
   +2 OTE zone | +2 HTF bias | +2 OB | +2 Liquidity swept | +2 BOS/CHoCH
   +1.5 FVG | +1 Prime session | +1 Volume | +1 DXY | +1 News clear
   State the grade: A+ / A / B / C / D

5. DO NOT give a specific trade plan (entry/SL/TP prices) from this
   screenshot — reading exact levels off an image is unreliable (this was
   tried and explicitly moved away from). Describe what you SEE
   qualitatively (structure, zones, candle patterns) only. For an actual
   entry/SL/target, Sir should use Live Monitor or ask "current setup?" in
   chat — those pull real OHLC data and run the validated
   Structure+Fibonacci+ADX math, not a visual read.

6. WHAT TO SAVE:
   - Any new rule, behavior, or ICT level identified from this chart
   - What pattern does this represent?

Be specific. Use exact price levels if readable. If chart is unclear, say so.
`;

export const buildPineScriptPrompt = (scriptCode) => `
Analyze this Pine Script indicator or strategy for XAU/USD trading.

PINE SCRIPT CODE:
\`\`\`
${scriptCode}
\`\`\`

Extract all tradeable intelligence:

1. CORE LOGIC:
   - What is this script trying to do?
   - What market condition does it identify?
   - What are the entry/exit conditions?

2. KEY LEVELS / CONDITIONS:
   - What price levels or calculations does it use?
   - What indicators does it combine?
   - What filters does it apply (time, session, volume, etc.)?

3. ICT / FIBONACCI CONNECTION:
   - Does this relate to any ICT concepts (OB, FVG, Liquidity, etc.)?
   - Does it use Fibonacci levels?
   - Does it consider market structure?

4. JARVIS RULES TO CREATE:
   Extract 1–5 specific trading rules from this script in this format:
   Rule: [title]
   Condition: [when to apply]
   Action: [BUY / SELL / WAIT]
   Notes: [any important details]

5. MARKET BEHAVIORS IDENTIFIED:
   What market behaviors is this script detecting?

6. INTEGRATION SUGGESTION:
   How should this be integrated with Sir's existing rule book?

Be thorough. Every line of logic in this script could become a JARVIS rule.
`;

// ─── Script Rule Extraction Prompt (returns JSON) ─────────────────────────
// Used when Sir pastes a script in JARVIS UI to auto-extract rules into Rule Book.
export const buildScriptExtractionPrompt = (scriptCode, scriptTitle = '') => `
You are JARVIS — XAU/USD AI. Sir has given you a Pine Script to study and extract rules from.
${scriptTitle ? `Script Title: "${scriptTitle}"\n` : ''}

PINE SCRIPT:
\`\`\`
${scriptCode}
\`\`\`

Extract ALL tradeable rules from this script. Return ONLY valid JSON (no markdown, no explanation):

{
  "summary": "2-3 sentence Marathi + English summary of what this script does",
  "core_concept": "The main idea of this script in one line",
  "rules": [
    {
      "title": "Rule name (short, clear)",
      "ict_concept": "OB / FVG / BOS / CHoCH / Liquidity / Fibonacci / ATR / Other",
      "condition": "Exact condition when this rule triggers (be specific)",
      "action": "BUY or SELL or WAIT or SKIP",
      "session": "Any or London or New York or Asian",
      "htf_bias": "BULLISH or BEARISH or Any",
      "sl_method": "Where to place stop loss",
      "tp_method": "Where to take profit",
      "rr_minimum": 2,
      "notes": "Important details Sir must know about this rule"
    }
  ],
  "key_behaviors": [
    "Market behavior 1 this script identifies",
    "Market behavior 2"
  ],
  "filters": [
    "Filter 1 (conditions that INVALIDATE the setup)",
    "Filter 2"
  ],
  "confluence_tip": "How this script's logic relates to the validated OTE zone (0.618-0.79 retracement) Jarvis actually trades from"
}

Extract 3–8 rules. Be specific. Every logic block in the script = one potential rule.
`;

// ─── Transcript / YouTube Knowledge Extraction Prompt ────────────────────────
// Used when Sir pastes a YouTube transcript or notes from any trading source.
// Extracts 5 categories of structured knowledge, saved as individual searchable chunks.
export const buildTranscriptExtractionPrompt = (text, source = '') => `
You are JARVIS — XAU/USD AI. Sir has given you a trading transcript/notes to extract structured knowledge from.
${source ? `Source: "${source}"\n` : ''}

TRANSCRIPT / TEXT (first 6000 chars):
${text.slice(0, 6000)}

Extract ALL useful trading knowledge. Return ONLY valid JSON (no markdown, no explanation):

{
  "source_summary": "1-2 line Marathi+English summary of what this source teaches",
  "key_concepts": [
    {
      "title": "Concept name (short)",
      "content": "Full explanation — be specific, include price levels if mentioned",
      "tags": ["ob", "ict", "entry"]
    }
  ],
  "rules": [
    {
      "title": "Rule name (actionable)",
      "content": "Exact rule — when to apply, what to do. Include all conditions.",
      "tags": ["entry", "ob", "london"]
    }
  ],
  "examples": [
    {
      "title": "Example title (pair + setup)",
      "content": "Specific market example with price levels, what happened, and what it teaches",
      "tags": ["xauusd", "h4", "ob"]
    }
  ],
  "mistakes": [
    {
      "title": "Mistake name",
      "content": "What the mistake is, why traders do it, and how to avoid it",
      "tags": ["psychology", "fomo", "overtrading"]
    }
  ],
  "checklist": [
    {
      "title": "Checklist item",
      "content": "What to check before/during/after a trade. Yes/No style.",
      "tags": ["pre-trade", "confirmation"]
    }
  ]
}

Rules:
- Maximum 8 items per category
- Be specific — generic statements are useless
- Tags must be lowercase, 1-3 word keywords relevant to XAU/USD ICT/SMC trading
- If source is ICT, focus on OB/FVG/Liquidity/BOS/CHoCH/AMD concepts
- If source is Price Action (Rayner Teo), focus on candlestick patterns + S/R levels
- Include exact price levels or percentages if mentioned in the source
`;

